// Multi-select Field Leader picker for the Overview Team section.
//
// Field leaders are assigned per-project (project_field_leaders). Assignment is
// what grants a field leader visibility + daily-log edit on the job, so the
// picker only offers members of the "Field Leaders" Entra group (read live via
// delegated Graph). Each assign/unassign returns the project's full updated list.
import { useState, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '@/lib/apiClient';
import { invalidateAssignmentQueries } from '@/lib/queryClient';
import { GraphConsentError } from '@/lib/graphUpload';
import { fetchGroupMembers, type GroupMember } from '@/lib/graphGroups';

// GroupMember.Read.All enumerates the group's members; User.ReadBasic.All is
// what actually returns their displayName/mail (with GroupMember.Read.All alone
// the member objects come back with id only and null profile fields).
const GRAPH_SCOPES = ['GroupMember.Read.All', 'User.ReadBasic.All'];
const GROUP_ID = import.meta.env.VITE_FIELD_LEADERS_GROUP_ID as string | undefined;

/** Lazily load the Field Leaders group's members via delegated Graph. Shared
 *  by this picker and the Mobilization modal's leader picker — one cache key,
 *  so opening either warms the other. Returns a disabled/idle query (no
 *  popup, no fetch) until `enabled` is true and the group id is configured. */
export function useFieldLeaderGroupMembers(enabled: boolean) {
  const { instance, accounts } = useMsal();
  const getToken = async (): Promise<string> => {
    try {
      const r = await instance.acquireTokenSilent({ scopes: GRAPH_SCOPES, account: accounts[0] });
      return r.accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        const r = await instance.acquireTokenPopup({ scopes: GRAPH_SCOPES });
        return r.accessToken;
      }
      throw new GraphConsentError('Graph consent required');
    }
  };
  return useQuery({
    queryKey: ['field-leader-group-members'],
    queryFn: () => fetchGroupMembers(getToken, GROUP_ID!),
    enabled: enabled && !!GROUP_ID,
    staleTime: 5 * 60_000,
  });
}

export default function FieldLeaderPicker({ projectId, canEdit, jobFolderUrl }: {
  projectId: number; canEdit: boolean;
  /** The project's linked SharePoint Job Folder (null = none linked). Drives
   *  the access reminder — assignment does NOT grant SharePoint access. */
  jobFolderUrl?: string | null;
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['field-leaders', projectId],
    queryFn: () => projectsApi.fieldLeaders(projectId),
  });
  const assigned = listQuery.data ?? [];
  const assignedIds = useMemo(() => new Set(assigned.map((f) => f.id)), [assigned]);

  // Group members are loaded lazily — only once the user opens the add control.
  const membersQuery = useFieldLeaderGroupMembers(adding);

  const candidates = useMemo(() => {
    const all = (membersQuery.data ?? []).filter((m) => !assignedIds.has(m.id));
    const q = query.trim().toLowerCase();
    const matched = q
      ? all.filter((m) => m.displayName.toLowerCase().includes(q) || (m.email ?? '').toLowerCase().includes(q))
      : all;
    return matched.slice(0, 12);
  }, [membersQuery.data, assignedIds, query]);

  const assignMut = useMutation({
    mutationFn: (m: GroupMember) =>
      projectsApi.assignFieldLeader(projectId, { entraObjectId: m.id, displayName: m.displayName, email: m.email ?? undefined }),
    onSuccess: (data) => {
      qc.setQueryData(['field-leaders', projectId], data);
      // ⚠️ A `project_field_leaders` row IS an assignment as far as the Today
      // gate is concerned — `activeAssignmentWhere` tests for one. This is not
      // dead code for field-leader-ONLY users, who never see Today: it is for
      // somebody who holds an office role AND sits in the Field Leaders group,
      // and who adds themselves here to their first live job. Covers
      // `project` / `projects` too. (Codex, #510.)
      void invalidateAssignmentQueries(qc, projectId);
      setQuery('');
      setError(null);
    },
    onError: (e: any) => setError(e?.message ?? 'Failed to assign'),
  });

  const removeMut = useMutation({
    mutationFn: (userId: string) => projectsApi.unassignFieldLeader(projectId, userId),
    onSuccess: (data) => {
      qc.setQueryData(['field-leaders', projectId], data);
      // Removing yourself from your last job takes the Today tab with it.
      void invalidateAssignmentQueries(qc, projectId);
      setError(null);
    },
    onError: (e: any) => setError(e?.message ?? 'Failed to remove'),
  });

  // Star = the project's primary leader (what the portfolio "Field:" shows).
  // The first person assigned is primary automatically; the star moves it.
  const primaryMut = useMutation({
    mutationFn: (userId: string) => projectsApi.setPrimaryFieldLeader(projectId, userId),
    onSuccess: (data) => {
      qc.setQueryData(['field-leaders', projectId], data);
      qc.invalidateQueries({ queryKey: ['project', projectId] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      setError(null);
    },
    onError: (e: any) => setError(e?.message ?? 'Failed to set primary'),
  });

  return (
    <div style={{ padding: 'var(--ew-s-4)', borderTop: '1px solid var(--ew-n-100)' }}>
      <label className="ov-label" style={{ display: 'block', marginBottom: 8 }}>Field Leaders</label>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {assigned.map((fl) => (
          <span key={fl.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 4px 3px 10px', background: fl.isPrimary ? 'var(--ew-green-50)' : 'var(--ew-n-100)', borderRadius: 'var(--ew-r-pill)', font: 'var(--ew-fw-medium) 13px var(--ew-font)' }}>
            {fl.displayName ?? fl.email}
            {fl.isPrimary ? (
              <span title="Primary field leader — shown on the Projects roll-up" aria-label="Primary field leader" style={{ color: 'var(--ew-green-700)', fontSize: 12, padding: '0 2px' }}>★</span>
            ) : canEdit && assigned.length > 1 ? (
              <button onClick={() => primaryMut.mutate(fl.id)} disabled={primaryMut.isPending} title="Make primary (shown on the Projects roll-up)" aria-label={`Make ${fl.displayName ?? fl.email} primary`}
                style={{ border: 0, background: 'none', cursor: 'pointer', color: 'var(--ew-n-400)', padding: '0 2px', lineHeight: 1, fontSize: 12 }}>☆</button>
            ) : null}
            {canEdit ? (
              <button onClick={() => removeMut.mutate(fl.id)} title="Remove" aria-label={`Remove ${fl.displayName ?? fl.email}`}
                style={{ border: 0, background: 'none', cursor: 'pointer', color: 'var(--ew-n-500)', padding: '0 4px', lineHeight: 1, fontSize: 15 }}>×</button>
            ) : null}
          </span>
        ))}
        {assigned.length === 0 ? <span style={{ color: 'var(--ew-n-500)', fontSize: 13 }}>{canEdit ? 'None assigned' : '—'}</span> : null}
      </div>

      {/* SharePoint is a separate grant: daily-log photo uploads run on the
          field leader's OWN delegated token, so assigning them here does NOT
          give them folder access — without it their uploads fail. Remind the
          PM whenever leaders are assigned. */}
      {assigned.length > 0 && canEdit ? (
        <div style={{
          marginTop: 10, padding: '8px 12px', borderRadius: 'var(--ew-r-md)', fontSize: 12, lineHeight: 1.5,
          background: 'var(--ew-safety-50, #FFF8E6)', border: '1px solid var(--ew-safety, #E8B931)', color: 'var(--ew-n-700)',
        }}>
          Assigning here grants Project Tracker access only — <b>daily-log photo uploads also need SharePoint access
          to the Job Folder</b>, granted separately.{' '}
          {jobFolderUrl
            ? <a href={jobFolderUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--ew-green-700)', fontWeight: 600 }}>Open the Job Folder</a>
            : <span>No Job Folder is linked to this project yet (Job Info section)</span>}
          {jobFolderUrl ? ' and use Share to add each field leader.' : ' — link it, then share it with each field leader.'}
        </div>
      ) : null}

      {canEdit ? (
        <div style={{ marginTop: 10 }}>
          {!adding ? (
            <button className="ew-btn-link" style={{ color: 'var(--ew-green)' }} onClick={() => { setAdding(true); setError(null); }}>+ Add field leader</button>
          ) : !GROUP_ID ? (
            <div className="ov-hint">The Field Leaders group isn't configured for this app (VITE_FIELD_LEADERS_GROUP_ID). Ask an admin to set it.</div>
          ) : (
            <div>
              <input className="ov-input" autoFocus placeholder="Search the Field Leaders group…" value={query} onChange={(e) => setQuery(e.target.value)} />
              {membersQuery.isError ? (
                <div className="ov-hint">{membersQuery.error instanceof GraphConsentError ? 'Group access is still being set up (admin consent pending).' : 'Could not load group members.'}</div>
              ) : membersQuery.isLoading ? (
                <div className="ov-hint">Loading group…</div>
              ) : (
                <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 220, overflowY: 'auto' }}>
                  {candidates.length === 0 ? (
                    <div className="ov-hint">No matching group members.</div>
                  ) : candidates.map((m) => (
                    <button key={m.id} onClick={() => assignMut.mutate(m)} disabled={assignMut.isPending}
                      style={{ textAlign: 'left', border: 0, background: 'none', cursor: 'pointer', padding: '6px 8px', borderRadius: 'var(--ew-r-md)', font: 'var(--ew-fw-regular) 13px var(--ew-font)' }}>
                      {m.displayName}{m.email ? <span style={{ color: 'var(--ew-n-500)' }}> · {m.email}</span> : null}
                    </button>
                  ))}
                </div>
              )}
              <div style={{ marginTop: 8 }}>
                <button className="ew-btn-link" style={{ color: 'var(--ew-n-500)' }} onClick={() => { setAdding(false); setQuery(''); }}>Done</button>
              </div>
            </div>
          )}
          {error ? <div className="ov-err" style={{ margin: '8px 0 0' }}>{error}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
